import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, ArrowUp, ArrowDown, Pencil, Archive, ArchiveRestore, Star, Film } from "lucide-react";
import { mediaUrl, fmtDuration } from "@/lib/academy";
import { AcademyLessonDialog } from "@/components/admin/AcademyLessonDialog";

const LessonRow = ({ l, i, last, onMove, onEdit, onStatus }) => (
  <div className={`p-3 sm:p-4 flex flex-wrap items-center gap-3 ${l.status === "archived" ? "opacity-60" : ""}`} data-testid={`academy-admin-row-${l.id}`}>
    <div className="flex flex-col">
      <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`academy-move-up-${l.id}`}><ArrowUp className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" className="h-7 w-7" disabled={last} onClick={() => onMove(i, 1)} data-testid={`academy-move-down-${l.id}`}><ArrowDown className="w-4 h-4" /></Button>
    </div>
    <div className="w-28 aspect-video rounded-md overflow-hidden bg-muted shrink-0">{l.thumbnail_url && <img src={mediaUrl(l.thumbnail_url)} alt="" className="w-full h-full object-cover" />}</div>
    <div className="flex-1 min-w-[180px] space-y-1">
      <div className="font-medium flex flex-wrap items-center gap-2">{l.title}{l.featured && <Star className="w-3.5 h-3.5 text-amber-500 fill-current" />}</div>
      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
        <span>{l.category}</span><span>{fmtDuration(l.duration)}</span>
        <span className="flex items-center gap-1"><Film className="w-3 h-3" />{l.video_url ? "Video uploaded" : "Video coming soon"}</span>
      </div>
    </div>
    {l.status === "archived" && <Badge variant="outline" data-testid={`academy-archived-badge-${l.id}`}>Archived</Badge>}
    <Button size="sm" variant="outline" onClick={() => onEdit(l)} data-testid={`academy-edit-${l.id}`}><Pencil className="w-4 h-4 mr-1" />Edit</Button>
    <Button size="sm" variant="ghost" onClick={() => onStatus(l, l.status === "archived" ? "active" : "archived")} data-testid={`academy-archive-${l.id}`}>
      {l.status === "archived" ? <><ArchiveRestore className="w-4 h-4 mr-1" />Restore</> : <><Archive className="w-4 h-4 mr-1" />Archive</>}
    </Button>
  </div>
);

export default function AcademyAdminPanel() {
  const [lessons, setLessons] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/academy/lessons"); setLessons(data.lessons); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const move = async (i, dir) => {
    const next = [...lessons];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    setLessons(next);
    try { await api.post("/admin/academy/reorder", { ids: next.map((l) => l.id) }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };

  const setStatus = async (l, status) => {
    try { await api.post(`/admin/academy/lessons/${l.id}/status`, { status }); toast.success(status === "archived" ? "Lesson archived" : "Lesson restored"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!lessons) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="academy-admin-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold">Partner Academy</h2>
          <p className="text-sm text-muted-foreground">Upload training videos, edit lessons, set the order partners see, and archive old lessons.</p>
        </div>
        <Button onClick={() => setEdit({})} data-testid="academy-new-lesson-btn"><Plus className="w-4 h-4 mr-1" />New lesson</Button>
      </div>
      <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid="academy-admin-list">
        {lessons.length === 0 ? <div className="p-8 text-center text-muted-foreground">No lessons yet.</div> : lessons.map((l, i) => (
          <LessonRow key={l.id} l={l} i={i} last={i === lessons.length - 1} onMove={move} onEdit={setEdit} onStatus={setStatus} />
        ))}
      </div>
      <AcademyLessonDialog lesson={edit?.id ? edit : null} open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
